import React from "react";
import {
  Heart, Syringe, Zap, Activity, FlaskConical, Pill,
  Stethoscope, Scissors, HeartPulse, FileText, Clock, Users, Trash2,
} from "lucide-react";

const ICONS = {
  code_started: { Icon: Clock,      color: "text-red-400" },
  cpr_started:  { Icon: HeartPulse, color: "text-red-400" },
  cpr_restart:  { Icon: HeartPulse, color: "text-rose-400" },
  code_end:     { Icon: Clock,      color: "text-slate-400" },
  pulse_check:  { Icon: HeartPulse, color: "text-sky-400" },
  rhythm:       { Icon: Activity,   color: "text-amber-300" },
  shock:        { Icon: Zap,        color: "text-amber-400" },
  epi:          { Icon: Syringe,    color: "text-emerald-400" },
  amiodarone:   { Icon: FlaskConical, color: "text-purple-400" },
  bicarb:       { Icon: Pill,       color: "text-teal-400" },
  calcium:      { Icon: Pill,       color: "text-yellow-400" },
  magnesium:    { Icon: Pill,       color: "text-cyan-400" },
  med_other:    { Icon: Syringe,    color: "text-emerald-400" },
  intubation:   { Icon: Stethoscope, color: "text-sky-400" },
  procedure:    { Icon: Scissors,   color: "text-slate-300" },
  rosc:         { Icon: Heart,      color: "text-rose-400" },
  note:         { Icon: FileText,   color: "text-slate-400" },
  personnel:    { Icon: Users,      color: "text-emerald-300" },
};

const UNDELETABLE = new Set(["cpr_started", "code_end"]);

const hhmmss = (iso) => new Date(iso).toLocaleTimeString("en-US", { hour12: false });

export default function CodeTimeline({ events, onDelete, maxHeightClass = "max-h-[60vh]" }) {
  return (
    <div className="bg-[#0e1a30] border border-[#1f2c47] rounded-2xl overflow-hidden" data-testid="timeline-panel">
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#1f2c47]">
        <span className="text-sm font-bold tracking-widest text-sky-300 uppercase">Timeline</span>
        <span className="font-mono text-xs text-slate-400">{events.length} events</span>
      </div>
      <div className={`${maxHeightClass} overflow-y-auto scroll-clean`}>
        {events.length === 0 && (
          <div className="text-slate-500 text-sm p-5 text-center">No events yet.</div>
        )}
        {events.slice().reverse().map((e) => {
          const meta = ICONS[e.type] || ICONS.note;
          const Icon = meta.Icon;
          const canDelete = typeof onDelete === "function" && !UNDELETABLE.has(e.type);
          return (
            <div
              key={e.id}
              className="group flex items-center gap-3 px-5 py-2.5 border-b border-[#1f2c47]/60 hover:bg-[#152340]/40"
              data-testid={`tl-${e.type}`}
            >
              <Icon className={`h-4 w-4 shrink-0 ${meta.color}`} strokeWidth={2} />
              <span className="font-mono text-xs text-slate-400 tabular-nums shrink-0">{hhmmss(e.ts)}</span>
              <span className={`text-sm font-semibold ${meta.color} flex-1 truncate`}>{e.label}</span>
              {canDelete && (
                <button
                  onClick={() => onDelete(e)}
                  data-testid={`tl-del-${e.id}`}
                  title="Delete event"
                  className="opacity-60 sm:opacity-0 sm:group-hover:opacity-100 focus:opacity-100 text-slate-500 hover:text-red-400 p-1.5 rounded transition-opacity"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
